import React, {useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import UserApi from "../services/users";
import Error from "../fragments/form/Error"
import Message from "../fragments/form/Message"
import TextValue from "../fragments/table/TextValue"
import AddToNetworkModal from '../components/AddToNetworkModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import './UsersTable.css';

function UsersTable (props) {
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [openDropdown, setOpenDropdownState] = useState(null); // Track which dropdown is open
    
    const setOpenDropdown = (userId) => {
        setOpenDropdownState(userId);
        
        // Auto-scroll and smart positioning to ensure dropdown is visible
        if (userId) {
            setTimeout(() => {
                const buttonElement = document.querySelector(`[data-user-id="${userId}"]`);
                if (buttonElement) {
                    const dropdown = buttonElement.nextElementSibling;
                    const tableWrap = document.querySelector('.table-wrap');
                    
                    if (dropdown && tableWrap) {
                        // Reset any custom positioning
                        dropdown.style.position = '';
                        dropdown.style.top = '';
                        dropdown.style.left = '';
                        dropdown.style.right = '';
                        dropdown.style.bottom = '';
                        dropdown.style.margin = '';
                        
                        // Check if this is the last row and if dropdown would be cut off
                        const buttonRect = buttonElement.getBoundingClientRect();
                        const tableWrapRect = tableWrap.getBoundingClientRect();
                        const dropdownHeight = 200; // Max height from CSS
                        const spaceBelow = tableWrapRect.bottom - buttonRect.bottom;
                        
                        // If not enough space below, scroll down to make room
                        if (spaceBelow < dropdownHeight) {
                            const scrollAmount = dropdownHeight - spaceBelow + 20; // Extra 20px buffer
                            tableWrap.scrollTop += scrollAmount;
                        }
                    }
                }
            }, 10); // Small delay to ensure dropdown is rendered
        }
    };
    const [showAddToNetworkModal, setShowAddToNetworkModal] = useState(false);
    const [selectedUserForNetwork, setSelectedUserForNetwork] = useState(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] = useState(null);
    const navigate = useNavigate();
    const userApi = new UserApi(props.token);
    const tenantId = props.tenantId || '1'; // Use passed tenantId or default
    const isNetworkView = props.isNetworkView || false; // Use passed isNetworkView or default

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.btn-group')) {
                setOpenDropdownState(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdown]);

    function viewUser(user) {
        // Navigate to user page with user data and current filter state
        navigate(`/user/${user.id}`, { 
            state: { 
                user: user,
                filters: {
                    search: props.search || '',
                    selectedNetwork: props.selectedNetwork || '',
                    activeLetter: props.activeLetter || 'all'
                }
            } 
        });
    }

    function editUser(user) {
        // Navigate to edit page with user data and current filter state
        navigate(`/user/${user.id}/edit`, { 
            state: { 
                user: user,
                filters: {
                    search: props.search || '',
                    selectedNetwork: props.selectedNetwork || '',
                    activeLetter: props.activeLetter || 'all'
                }
            } 
        });
    }

    function removeUserFromTenant(userId, userName, userNetworks = null){
        // Determine which network to remove from
        let targetTenantId = tenantId;
        let networkName = "the network";
        
        // If we're not in a specific network context and user has networks, use the first one
        if (!isNetworkView && userNetworks && userNetworks.length > 0) {
            targetTenantId = userNetworks[0].tenant_id;
            networkName = userNetworks[0].tenant_name || "the network";
        }
        
        // Show confirmation dialog
        const isConfirmed = window.confirm(
            `Are you sure you want to remove "${userName}" from ${networkName}?\n\nThis action will remove the user from the network but will not delete their account.`
        );
        
        if (!isConfirmed) {
            return; // User cancelled the action
        }
        
        userApi.removeMember(targetTenantId, userId).then((response) => {
            setMessage(`User "${userName}" has been removed from ${networkName} successfully`);
            setError("");
            props.refresh();
            
            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                setMessage("");
            }, 3000);
        })
        .catch((err) => {
            console.error(err.message);
            const errorMessage = err.response?.data?.message || err.message;
            setError(errorMessage);
            setMessage("");
        });
    }

    function approveUser(userId, userName){
        userApi.approveMember(tenantId, userId).then((response) => {
            setMessage(`User "${userName}" has been approved`)
            setError("");
            props.refresh();
            
            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                setMessage("");
            }, 3000);
        })
        .catch((err) => {
            console.error(err.message);
            const errorMessage = err.response?.data?.message || err.message;
            setError(errorMessage);
            setMessage("");
        });
    }

    function rejectUser(userId, userName){
        userApi.rejectMember(tenantId, userId).then((response) => {
            setMessage(`User "${userName}" has been rejected`)
            setError("");
            props.refresh();
            
            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                setMessage("");
            }, 3000);
        })
        .catch((err) => {
            console.error(err.message);
            const errorMessage = err.response?.data?.message || err.message;
            setError(errorMessage);
            setMessage("");
        });
    }

    function sendPasswordReset(userEmail, userName){
        userApi.sendPasswordReset(tenantId, userEmail).then((response) => {
            setMessage(`Password reset email sent to "${userName}"`)
            setError("");
            
            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                setMessage("");
            }, 3000);
        })
        .catch((err) => {
            console.error(err.message);
            const errorMessage = err.response?.data?.message || err.message;
            setError(errorMessage);
            setMessage("");
        });
    }

    function deactivateUserAccount(user) {
        const userName = `${user.first_name} ${user.last_name}`.trim() || user.email;
        const isConfirmed = window.confirm(
            `Deactivate "${userName}"?\n\nThis will disable their access, remove active tenant memberships, and drop any admin record while keeping their email on file.`
        );

        if (!isConfirmed) {
            return;
        }

        setOpenDropdownState(null);
        userApi.deactivateUser(user.id)
        .then(() => {
            setMessage(`User "${userName}" has been deactivated`);
            setError("");
            props.refresh();
            setTimeout(() => setMessage(""), 3000);
        })
        .catch((err) => {
            console.error(err.message);
            const errorMessage = err.response?.data?.message || err.message;
            setError(errorMessage);
            setMessage("");
        });
    }

    function deleteUserAccount(user) {
        const userName = `${user.first_name} ${user.last_name}`.trim() || user.email;
        const isConfirmed = window.confirm(
            `Permanently delete "${userName}"?\n\nThis will deactivate the user if needed, remove memberships and admin access, and rename their email so it can be reused. This action cannot be undone.`
        );

        if (!isConfirmed) {
            return;
        }

        setOpenDropdownState(null);
        userApi.deleteUser(user.id)
        .then(() => {
            setMessage(`User "${userName}" has been deleted`);
            setError("");
            props.refresh();
            setTimeout(() => setMessage(""), 3000);
        })
        .catch((err) => {
            console.error(err.message);
            const errorMessage = err.response?.data?.message || err.message;
            setError(errorMessage);
            setMessage("");
        });
    }

    function openAddToNetworkModal(user) {
        setSelectedUserForNetwork(user);
        setShowAddToNetworkModal(true);
        setOpenDropdown(null); // Close the dropdown
    }

    function closeAddToNetworkModal() {
        setShowAddToNetworkModal(false);
        setSelectedUserForNetwork(null);
    }

    function handleAddToNetworkSuccess(successMessage) {
        // Show success message above the table
        setMessage(successMessage);
        setError("");
        
        // Auto-clear success message after 3 seconds
        setTimeout(() => {
            setMessage("");
        }, 3000);
        
        // Refresh the users list
        props.refresh();
    }

    function openChangePasswordModal(user) {
        setSelectedUserForPasswordChange(user);
        setShowChangePasswordModal(true);
        setOpenDropdown(null); // Close the dropdown
    }

    function closeChangePasswordModal() {
        setShowChangePasswordModal(false);
        setSelectedUserForPasswordChange(null);
    }

    function handleChangePasswordSuccess(successMessage) {
        // Show success message above the table
        setMessage(successMessage);
        setError("");
        
        // Auto-clear success message after 3 seconds
        setTimeout(() => {
            setMessage("");
        }, 3000);
    }

    // Determine if we're showing network members (with approval status) or all users
    const showNetworkView = isNetworkView;
    
    return(
        <div>
            <Error message={error} />
            <Message message={message} />
            <div className="table-wrap">
                <table className="users-table">
                    <colgroup>
                        <col style={{width: '80px'}} />
                        <col />
                        <col />
                        <col style={{width: '120px'}} />
                        <col style={{width: '160px'}} />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>NAME</th>
                            <th>EMAIL</th>
                            <th>{showNetworkView ? 'ROLE' : 'SUPERUSER'}</th>
                            <th>STATUS</th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody className="users-body">
                        {props.users.map((user, index) => (
                            <tr key={index}>
                                <td><TextValue value={user.id} /></td>
                                <td title={`${user.first_name || ''} ${user.last_name || ''}`.trim()}>
                                    <TextValue value={`${user.first_name || ''} ${user.last_name || ''}`.trim()} />
                                </td>
                                <td title={user.email}>
                                    <TextValue value={user.email} />
                                </td>
                                <td>
                                {showNetworkView ? (
                                    // Show network role for network view
                                    user.role?.name || (user.networks && user.networks.length > 0 ? user.networks[0].role_name : 'No role')
                                ) : (
                                    // Show superuser status for all users view
                                    <span className={`badge ${user.is_superuser ? 'bg-primary text-white' : 'bg-light text-dark'}`}>
                                        {user.is_superuser ? 'Yes' : 'No'}
                                    </span>
                                )}
                                </td>
                                <td>
                                {showNetworkView ? (
                                    // Show membership status for network view
                                    user.membership_status ? (
                                        <span className={`badge ${
                                            user.membership_status === 'approved' ? 'bg-success' : 
                                            user.membership_status === 'pending' ? 'bg-warning' : 
                                            user.membership_status === 'rejected' ? 'bg-danger' : 'bg-secondary'
                                        }`}>
                                            {user.membership_status}
                                        </span>
                                    ) : (user.networks && user.networks.length > 0 ? (
                                        <span className={`badge ${
                                            user.networks[0].status === 'approved' ? 'bg-success' : 
                                            user.networks[0].status === 'pending' ? 'bg-warning' : 
                                            user.networks[0].status === 'rejected' ? 'bg-danger' : 'bg-secondary'
                                        }`}>
                                            {user.networks[0].status}
                                        </span>
                                    ) : (
                                        <span className="badge bg-secondary">No Network</span>
                                    ))
                                ) : (
                                    // Show general status for all users view
                                    <span className={`badge ${user.status ? 'bg-success' : 'bg-secondary'}`}>
                                        {user.status ? 'Active' : 'Not Active'}
                                    </span>
                                )}
                                </td>
                                <td>
                                <div className="btn-group" style={{ position: 'relative' }}>
                                    <button 
                                        className="btn btn-sm btn-outline-secondary dropdown-toggle" 
                                        type="button" 
                                        data-user-id={user.id}
                                        onClick={() => setOpenDropdown(openDropdown === user.id ? null : user.id)}
                                    >
                                        Actions
                                    </button>
                                    {openDropdown === user.id && (
                                        <ul className="dropdown-menu" style={{ 
                                            position: 'absolute', 
                                            top: '100%', 
                                            right: 0, 
                                            zIndex: 9999,
                                            display: 'block',
                                            minWidth: '160px',
                                            padding: '0.5rem 0',
                                            margin: '0.125rem 0 0',
                                            backgroundColor: '#fff',
                                            border: '1px solid rgba(0,0,0,.15)',
                                            borderRadius: '0.375rem',
                                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.175)',
                                            maxHeight: '200px',
                                            overflowY: 'auto'
                                        }}>
                                            <li>
                                                <button 
                                                    className="dropdown-item" 
                                                    style={{ 
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '0.25rem 1rem',
                                                        clear: 'both',
                                                        fontWeight: '400',
                                                        color: '#212529',
                                                        textAlign: 'inherit',
                                                        textDecoration: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: 'transparent',
                                                        border: '0',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        setOpenDropdownState(null);
                                                        viewUser(user);
                                                    }}
                                                >
                                                    View
                                                </button>
                                            </li>
                                            <li>
                                                <button 
                                                    className="dropdown-item" 
                                                    style={{ 
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '0.25rem 1rem',
                                                        clear: 'both',
                                                        fontWeight: '400',
                                                        color: '#212529',
                                                        textAlign: 'inherit',
                                                        textDecoration: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: 'transparent',
                                                        border: '0',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        setOpenDropdownState(null);
                                                        editUser(user);
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                            </li>
                                            <li>
                                                <button 
                                                    className="dropdown-item" 
                                                    style={{ 
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '0.25rem 1rem',
                                                        clear: 'both',
                                                        fontWeight: '400',
                                                        color: '#212529',
                                                        textAlign: 'inherit',
                                                        textDecoration: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: 'transparent',
                                                        border: '0',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        setOpenDropdownState(null);
                                                        openAddToNetworkModal(user);
                                                    }}
                                                >
                                                    Add to Network
                                                </button>
                                            </li>
                                            <li>
                                                <button 
                                                    className="dropdown-item" 
                                                    style={{ 
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '0.25rem 1rem',
                                                        clear: 'both',
                                                        fontWeight: '400',
                                                        color: '#212529',
                                                        textAlign: 'inherit',
                                                        textDecoration: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: 'transparent',
                                                        border: '0',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        setOpenDropdownState(null);
                                                        sendPasswordReset(user.email, `${user.first_name} ${user.last_name}`);
                                                    }}
                                                >
                                                    Send Password Reset
                                                </button>
                                            </li>
                                            <li>
                                                <button 
                                                    className="dropdown-item" 
                                                    style={{ 
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '0.25rem 1rem',
                                                        clear: 'both',
                                                        fontWeight: '400',
                                                        color: '#212529',
                                                        textAlign: 'inherit',
                                                        textDecoration: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: 'transparent',
                                                        border: '0',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        setOpenDropdownState(null);
                                                        openChangePasswordModal(user);
                                                    }}
                                                >
                                                    Change Password
                                                </button>
                                            </li>
                                            {user.status && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-warning"
                                                        style={{
                                                            display: 'block',
                                                            width: '100%',
                                                            padding: '0.25rem 1rem',
                                                            clear: 'both',
                                                            fontWeight: '400',
                                                            color: '#ffc107',
                                                            textAlign: 'inherit',
                                                            textDecoration: 'none',
                                                            whiteSpace: 'nowrap',
                                                            backgroundColor: 'transparent',
                                                            border: '0',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => deactivateUserAccount(user)}
                                                    >
                                                        Deactivate User
                                                    </button>
                                                </li>
                                            )}
                                            {showNetworkView && user.membership_status === 'pending' && (
                                                <>
                                                    <li><hr style={{ margin: '0.5rem 0', border: '0', borderTop: '1px solid rgba(0,0,0,.1)' }} /></li>
                                                    <li>
                                                        <button 
                                                            className="dropdown-item text-success" 
                                                            style={{ 
                                                                display: 'block',
                                                                width: '100%',
                                                                padding: '0.25rem 1rem',
                                                                clear: 'both',
                                                                fontWeight: '400',
                                                                color: '#198754',
                                                                textAlign: 'inherit',
                                                                textDecoration: 'none',
                                                                whiteSpace: 'nowrap',
                                                                backgroundColor: 'transparent',
                                                                border: '0',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => {
                                                                setOpenDropdownState(null);
                                                                approveUser(user.id, `${user.first_name} ${user.last_name}`);
                                                            }}
                                                        >
                                                            Approve User
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button 
                                                            className="dropdown-item text-danger" 
                                                            style={{ 
                                                                display: 'block',
                                                                width: '100%',
                                                                padding: '0.25rem 1rem',
                                                                clear: 'both',
                                                                fontWeight: '400',
                                                                color: '#dc3545',
                                                                textAlign: 'inherit',
                                                                textDecoration: 'none',
                                                                whiteSpace: 'nowrap',
                                                                backgroundColor: 'transparent',
                                                                border: '0',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => {
                                                                setOpenDropdownState(null);
                                                                rejectUser(user.id, `${user.first_name} ${user.last_name}`);
                                                            }}
                                                        >
                                                            Reject User
                                                        </button>
                                                    </li>
                                                </>
                                            )}
                                            {user.networks && user.networks.length > 0 && (
                                                <>
                                                    <li><hr style={{ margin: '0.5rem 0', border: '0', borderTop: '1px solid rgba(0,0,0,.1)' }} /></li>
                                                    <li>
                                                        <button 
                                                            className="dropdown-item text-danger" 
                                                            style={{ 
                                                                display: 'block',
                                                                width: '100%',
                                                                padding: '0.25rem 1rem',
                                                                clear: 'both',
                                                                fontWeight: '400',
                                                                color: '#dc3545',
                                                                textAlign: 'inherit',
                                                                textDecoration: 'none',
                                                                whiteSpace: 'nowrap',
                                                                backgroundColor: 'transparent',
                                                                border: '0',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => {
                                                                setOpenDropdownState(null);
                                                                removeUserFromTenant(user.id, `${user.first_name} ${user.last_name}`, user.networks);
                                                            }}
                                                        >
                                                            Remove from Network
                                                        </button>
                                                    </li>
                                                </>
                                            )}
                                            <li><hr style={{ margin: '0.5rem 0', border: '0', borderTop: '1px solid rgba(0,0,0,.1)' }} /></li>
                                            <li>
                                                <button
                                                    className="dropdown-item text-danger"
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '0.25rem 1rem',
                                                        clear: 'both',
                                                        fontWeight: '400',
                                                        color: '#dc3545',
                                                        textAlign: 'inherit',
                                                        textDecoration: 'none',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: 'transparent',
                                                        border: '0',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => deleteUserAccount(user)}
                                                >
                                                    Delete User
                                                </button>
                                            </li>
                                        </ul>
                                    )}
                                </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Add to Network Modal */}
            <AddToNetworkModal
                isOpen={showAddToNetworkModal}
                onClose={closeAddToNetworkModal}
                user={selectedUserForNetwork}
                token={props.token}
                onSuccess={handleAddToNetworkSuccess}
            />
            
            {/* Change Password Modal */}
            <ChangePasswordModal
                isOpen={showChangePasswordModal}
                onClose={closeChangePasswordModal}
                user={selectedUserForPasswordChange}
                token={props.token}
                onSuccess={handleChangePasswordSuccess}
            />
        </div>
    )
}

export default UsersTable
